<template>
  <div class="flex w-full h-full overflow-hidden">
    <loader v-if="loading" />
    <template v-else>
      <!-- IMAGES & REQUESTS -->
      <div class="flex flex-col w-1/2 h-full">
        <!-- TOOLBAR (TABS) -->
        <div class="h-full flex flex-col px-5 pt-5">
          <div class="flex w-full h-8">
            <tx-tabs v-model="activeTab" class="grow" :tabs="Object.values(tabs)" />
            <settings-dropdown module="articleDetails" :disable-live-updates="!allowArticleCacheUpdatedEventStream" @change="onSettingsChanged" />
          </div>
          <transition
            enter-from-class="opacity-0" enter-active-class="ease-out transition-medium" enter-to-class="opacity-100"
            leave-from-class="opacity-100" leave-active-class="ease-out transition-medium" leave-to-class="opacity-0"
          >
            <article-resources v-if="activeTab === tabs.resources.key" :article="currentArticle" />

            <audit-trails v-else-if="activeTab === tabs.history.key" :article="currentArticle" />

            <manage-object-relation v-else-if="activeTab === tabs.related.key" :article="currentArticle" />

            <design-options v-else-if="activeTab === tabs.designOptions.key" :article="currentArticle" :design-option-added="newDesignOptionsAdded" :show-both-article-and-model-d-o="true" @design-options-status-updated="designOptionsStatusUpdated" />

            <div v-else class="flex flex-col mt-2 h-full">
              <!-- HEADER -->
              <div class="flex flex-col w-full">
                <flags-band :article="currentArticle" :flags="getFlags" />
                <div class="w-full pt-2">
                  <span class="inline-block w-5/6 text-2xl font-semibold select-all whitespace-nowrap text-ellipsis">{{ currentArticle.ArticleName }}</span>
                  <span class="inline-block w-1/6 text-sm font-semibold text-right select-all text-primary-500">{{ getRetailPrice }}</span>
                </div>
                <div class="w-full pt-1">
                  <span class="inline-block w-5/6">
                    <span class="text-sm select-all text-grey-icon whitespace-nowrap text-ellipsis">{{ currentArticle.ArticleNumber }}</span>
                    <span v-if="currentArticle.Status === 0" class="px-1 ml-1 text-xs text-white align-middle bg-red-500 rounded-lg">{{ t('general.dropped') }}</span>
                    <span v-if="currentArticle.Status === 2" class="px-1 ml-1 text-xs text-white align-middle bg-blue-500 rounded-lg">{{ t('general.notAssorted') }}</span>
                    <span v-if="currentArticle.Status === 3" class="px-1 ml-1 text-xs text-white align-middle bg-red-500 rounded-lg">{{ t('general.globallyDropped') }}</span>
                  </span>
                  <span class="inline-block w-1/6 text-sm text-right select-all text-grey-icon">{{ getDate }}</span>
                </div>
                <!-- TODO: Work on request article header -->
              </div>

              <!-- Assets -->
              <div class="flex flex-col w-full overflow-hidden">
                <article-assets-viewer :article="currentArticle" :style="{ height: availableArticleAssetsViewerHeight }" @asset-status-updated="assetsStatusUpdated" />
                <div v-if="!currentArticle._IsRequestArticle && userStore.activeCatalog?.CatalogCode === currentArticle.CatalogCode && selectedArticleOnMerchOrWhiteboardForSwap && currentArticle.Id !== selectedArticleOnMerchOrWhiteboardForSwap" v-tooltip="{ text: t('general.swapArticle'), theme: { placement: 'top' } }" class="w-[10px] pt-2 pr-2">
                  <font-awesome-icon icon="fa-light fa-exchange" class="w-4 h-4 ml-0.5 cursor-pointer hover:text-primary-500" @click="swapArticle" />
                </div>
                <!-- Other colors section  -->
                <div v-if="showOtherColors" class="flex flex-col w-full relative">
                  <div v-if="isOtherColorsloading" class="pt-10">
                    <loader class="absolute inset-0 flex items-center justify-center" :width="30" :height="30" />
                  </div>
                  <tx-horizontal-scroll v-else class="relative h-25">
                    <div
                      v-for="art in otherColorArticles"
                      v-show="art.CatalogArticleId !== article.CatalogArticleId"
                      :key="art.CatalogArticleId" class="inline-block m-1 cursor-pointer h-25 w-25"
                      @click="onOtherColorArticles(art)"
                    >
                      <div class="h-15">
                        <image-loader
                          :context="article.CatalogCode === userStore.activeCatalog?.CatalogCode ? userStore.activeCatalog.DuneContext : userStore.linkedCatalogDetails[article.CatalogCode].DuneContext"
                          :context-key="article.CatalogCode === userStore.activeCatalog?.CatalogCode ? userStore.activeCatalog.ContextKey : userStore.linkedCatalogDetails[article.CatalogCode].ContextKey"
                          :article-number="art.ArticleNumber"
                          :article-id="art.Id"
                          :is-image-allowed="true"
                          :size="95"
                        />
                      </div>
                      <div class="pl-2 h-auto text-xs text-center break-words">
                        {{ otherColorConfiguredAttributeValues && otherColorConfiguredAttributeValues[art.Id] ? otherColorConfiguredAttributeValues[art.Id] : '-' }}
                      </div>
                    </div>
                  </tx-horizontal-scroll>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
      <!-- TODO: REQUESTS -->

      <!-- ARTICLE DETAILS AND FORMS -->
      <div class="w-1/2 h-full px-2 py-4 bg-default">
        <div class="flex h-10 p-2 mr-24">
          <tx-button v-show="showBackArrow" type="icon" faicon="fa-light fa-chevron-left" @click="onBackClicked" />

          <!-- ACTIONS HEADER -->
          <div class="flex flex-col mx-2 my-[30px] grow-0 shrink-0 justify-center header">
            <!-- TITLE -->
            <div class="flex-1 text-xl font-semibold text-center text-tx-form-title" v-text="infoHeaderTitle" />
            <span v-if="selectedArticleAction === allArticleActions.assignColor.key" class="mt-3 text-base text-center lex-1 text-tx-form-title">{{ t('assignColor.header') }}</span>
          </div>

          <!-- ARTICLE ACTIONS DROPDOWN -->
          <tx-dropdown
            v-if="isArticleActionsEnabled && !showBackArrow && !readOnly" v-model="selectedArticleAction"
            :items="getArticleActions" value-prop="key" display-prop="label"
          >
            <template #button>
              <icon-button
                type="inline" faicon="fa-light fa-angle-down" :label="t('general.actions', getArticleActions.length)"
                :right-icon="true" :width="100"
              />
            </template>
          </tx-dropdown>
        </div>

        <!-- BODY -->
        <div class="w-full h-full pb-4 mb-2 mt-5">
          <!-- TODO: local forecast and notes -->
          <update-article
            v-if="selectedArticleAction === allArticleActions.edit.key" ref="updateArticleRef" :articles="[currentArticle]"
            :show-header="false" @cancel="onBackClicked" @updated="onArticleUpdated"
          />

          <active-or-deactive-article
            v-else-if="selectedArticleAction === allArticleActions.activate.key || selectedArticleAction === allArticleActions.deactivate.key"
            :show-header="false" :type="selectedArticleAction === allArticleActions.activate.key ? 'activate' : 'deactivate'"
            :articles="[currentArticle]" @cancel="onBackClicked" @updated="onArticleUpdated"
          />
          <lock-or-unlock-article
            v-else-if="selectedArticleAction === allArticleActions.lock.key || selectedArticleAction === allArticleActions.unlock.key"
            :type="selectedArticleAction === allArticleActions.lock.key ? 'lock' : 'unlock'" :show-header="false"
            :articles="[currentArticle]" @cancel="onBackClicked" @updated="onArticleUpdated"
          />

          <update-article-segmentation
            v-else-if="selectedArticleAction === allArticleActions.editSegmentations.key"
            :show-header="false" :articles="[currentArticle]" @cancel="onBackClicked" @updated="onArticleUpdated"
          />

          <update-article-retail-window
            v-else-if="selectedArticleAction === allArticleActions.editRetailWindow.key"
            :show-header="false" :articles="[currentArticle]" @cancel="onBackClicked" @updated="onArticleUpdated"
          />

          <update-article-forecast
            v-else-if="selectedArticleAction === allArticleActions.editForecasts.key"
            :article="currentArticle" @cancel="onBackClicked" @updated="onArticleUpdated"
          />

          <assign-article-resource
            v-else-if="selectedArticleAction === allArticleActions.assignResources.key"
            :show-header="false" :articles="[currentArticle]" @cancel="onBackClicked" @updated="onArticleUpdated"
          />

          <manage-object-relation
            v-else-if="selectedArticleAction === allArticleActions.editRelations.key"
            :article="currentArticle" @cancel="onBackClicked"
          />

          <update-size-scale
            v-else-if="selectedArticleAction === allArticleActions.editSizes.key" :articles="[currentArticle]"
            :show-header="false" @cancel="onBackClicked" @updated="onArticleUpdated"
          />

          <update-prices
            v-else-if="selectedArticleAction === allArticleActions.editPrices.key" :articles="[currentArticle]"
            :show-header="false" :is-articles-locked="doesArticleLockedDueToAnyModelArticle" @cancel="onBackClicked" @updated="onArticleUpdated"
          />

          <assign-color
            v-else-if="selectedArticleAction === allArticleActions.assignColor.key" :context-article="currentArticle"
            :show-header="false" @cancel="onBackClicked" @updated="onArticleUpdated"
          />

          <update-period
            v-else-if="selectedArticleAction === allArticleActions.editPeriod.key" :article="currentArticle"
            :show-header="false" @cancel="onBackClicked" @updated="onArticleUpdated"
          />

          <manage-article-state
            v-else-if="selectedArticleAction === allArticleActions.manageArticleState.key"
            :show-header="false" :articles="[currentArticle]" @cancel="onBackClicked" @updated="onArticleUpdated"
          />

          <update-parent-model
            v-else-if="selectedArticleAction === allArticleActions.editParentModel.key"
            :show-header="false" :articles="[currentArticle]" @cancel="onBackClicked" @updated="onArticleUpdated"
          />

          <assort-articles
            v-if="selectedArticleAction === allArticleActions.assortArticles.key" :show-header="false"
            :articles="[currentArticle]" @cancel="onBackClicked" @updated="onArticleUpdated"
          />

          <div v-else class="w-full h-full overflow-y-auto">
            <!-- ALERT -->
            <tx-alert :show="infoMessageList.length > 0" type="info" :text="infoMessageList.join(', ')" />

            <!-- FAVORITES -->
            <article-favorites v-if="!readOnly" :article="currentArticle" @on-edit-fav="onEditFav" />

            <!-- ATTRIBUTES -->
            <tx-collapse v-model="activeGroups" class="mb-4">
              <tx-collapse-item v-for="(group, key) in articleDetailsData.groups" :key="key" title-class="tracking-[1px] font-bold  text-primary-500" header-class="bg-white" :title="key.toString()" :value="key">
                <div class="flex flex-row flex-wrap p-4 attributes">
                  <article-info-cell
                    v-for="(itm, index) in group" :key="index" :label="itm.label" :value="itm.value"
                    :width="itm.width" :style="itm.style"
                  />
                </div>
              </tx-collapse-item>
            </tx-collapse>

            <div class="flex flex-row flex-wrap p-4 attributes">
              <article-info-cell
                v-for="(itm, index) in articleDetailsData.nonGroup" :key="index" :label="itm.label" :value="itm.value"
                :width="itm.width" :style="itm.style"
              />
            </div>

            <!-- STOCKS -->
            <stock-table v-if="userStore.activeCatalog?.IsStockApply" :article="currentArticle" />

            <!-- ATTACHMENTS -->

            <!-- FORECAST -->
            <update-article-forecast v-if="isUpdateArticleForecastEnabled" class="h-auto my-2" :article="currentArticle" readonly />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import type { ISettingItem } from './components/SettingsDropdown.vue'
import SettingsDropdown from './components/SettingsDropdown.vue'
import LockOrUnlockArticle from './components/LockOrUnlockArticle.vue'
import ArticleAssetsViewer from './components/ArticleAssetsViewer.vue'
import ArticleFavorites from './components/ArticleFavorites.vue'
import ArticleInfoCell from './components/ArticleInfoCell.vue'
import ArticleResources from './components/ArticleResources.vue'
import AuditTrails from './components/AuditTrails.vue'
import UpdateArticle from './components/UpdateArticle.vue'
import ActiveOrDeactiveArticle from './components/ActiveOrDeactiveArticle.vue'
import UpdateArticleSegmentation from './components/UpdateArticleSegmentation.vue'
import UpdateArticleForecast from './components/UpdateArticleForecast.vue'
import AssignArticleResource from './components/AssignArticleResource.vue'
import ManageArticleState from './components/ManageArticleState.vue'
import updatePrices from './components/UpdatePrices.vue'
import UpdatePeriod from './components/UpdatePeriod.vue'
import AssignColor from './components/AssignColor.vue'
import ManageObjectRelation from './components/ManageObjectRelation.vue'
import UpdateSizeScale from './components/UpdateSizeScale.vue'
import UpdateParentModel from './components/UpdateParentModel.vue'
import StockTable from './components/StockTable.vue'
import AssortArticles from './components/AssortArticles.vue'
import useArticleActions from './composables/articleActions'
import DesignOptions from '@/modules/articleDetails/components/DesignOptions.vue'
import Loader from '@/shared/components/Loader.vue'
import Article from '@/models/article'
import MyArticle from '@/models/myArticle'
import { privileges } from '@/models/constants'
import utils from '@/services/utils'
import appConfig from '@/services/appConfig'
import UpdateArticleRetailWindow from '@/shared/components/UpdateArticleRetailWindow.vue'
import ImageLoader from '@/shared/components/ImageLoader.vue'
import TxHorizontalScroll from '@/shared/components/TxHorizontalScroll.vue'
import TxCollapse from '@/shared/components/txCollapse/txCollapse.vue'
import TxCollapseItem from '@/shared/components/txCollapse/txCollapseItem.vue'
import FlagsBand from '@/shared/components/FlagsBand.vue'
import IconButton from '@/shared/components/IconButton.vue'
import TxAlert from '@/shared/components/TxAlert.vue'
import TxDropdown from '@/shared/components/TxDropdown.vue'
import TxTabs from '@/shared/components/TxTabs.vue'
import TxButton from '@/shared/components/TxButton.vue'
import useDisableModule from '@/shared/composables/disableModule'
import { useNotificationStore } from '@/store/notification'
import { useUserStore } from '@/store/userData'
import { getArticleAssets, getArticlesStream } from '@/api/t1/article'
import { useArticleStore } from '@/store/article'
import { useArticleFormHelper } from '@/shared/composables/articleFormHelper'
import { useConfirmDialog } from '@/shared/composables/confirmDialog'
import type DuneAsset from '@/models/duneAsset'
import net from '@/services/net'

interface IProps {
  article: MyArticle
  readOnly?: boolean
  designOptionAdded?: string
}

const props = withDefaults(defineProps<IProps>(), {
  readOnly: false,
  designOptionAdded: '',
})

const emit = defineEmits<{
  (e: 'updated', key: string, value: any): void
  (e: 'change', article: MyArticle): void
  (e: 'onUpdatedArticles', articles: MyArticle[]): void
  (e: 'onEditFav', article: MyArticle): void
  (e: 'articleAssetsUpdated', articleId: number, assets: DuneAsset[]): void
  (e: 'swapArticle', targetArticle: MyArticle): void
  (e: 'designOptionDeleted', assetId: string, isArticleDesignOption: boolean, article: MyArticle): void
}>()

const { t } = useI18n()
const userStore = useUserStore()
const articleStore = useArticleStore()
const notificationStore = useNotificationStore()
const confirmDialog = useConfirmDialog()

const currentArticle = ref<MyArticle>(props.article)
const doesArticleLockedDueToAnyModelArticle = ref(false)
// user settings
const showOtherColors = computed(() => utils.isDefined(userStore.settingsContainer) && utils.isDefined(userStore.settingsContainer.general) && utils.isDefined(userStore.settingsContainer.general.articleDetailsAvailableColors) ? userStore.settingsContainer.general.articleDetailsAvailableColors : true)
const newDesignOptionsAdded = computed(() => props.designOptionAdded)

const { isResourcesModuleEnabled, isOrderModuleEnabled, isForecastModuleEnabled } = useDisableModule()
const { selectedArticleAction, allArticleActions, getArticleActions, getUpdatedMessage } = useArticleActions(currentArticle, doesArticleLockedDueToAnyModelArticle)
const { getArticlesLocked } = useArticleFormHelper()

const tabs: Record<string, ITabItem> = {
  images: { key: 'images', label: 'general.images', visible: true },
  resources: { key: 'resources', label: 'general.resources', visible: isResourcesModuleEnabled.value },
  history: { key: 'history', label: 'general.history', visible: userStore.userProfile.isValidPrivilege(privileges.article.getHistory) },
  related: { key: 'related', label: 'general.related', visible: userStore.userProfile.isValidPrivilege(privileges.objectRelation.get) },
  designOptions: { key: 'designOptions', label: 'articleDetails.designOptions', visible: true },
}

const updateArticleRef = ref<InstanceType<typeof UpdateArticle>>()
const loading = ref(false)
const activeTab = ref(tabs.images.key)
const otherColorArticles = ref<MyArticle[]>([])
const otherColorConfiguredAttributeValues = ref<Record<string, any>>({})
const activeGroups = ref<Array<string>>([])
const articleDetailsData = ref<Record<any, any>>({})
const selectedArticleOnMerchOrWhiteboardForSwap = ref<number | null>(null)
const isOtherColorsloading = ref(false)
const liveUpdates = ref(true)
let eventSource: EventSource | null = null
let pEventSource: EventSource | null = null
let explicitlyClosedES = false

const getFlags = computed(() => {
  if (userStore.activeCatalog?.Config.ArticleThumbnailFlags) { return userStore.activeCatalog?.Config.ArticleThumbnailFlags }

  else { return [] as IFlagsBandFlag[] }
})

const getRetailPrice = computed(() => {
  return userStore.activeCatalog?.IsLinePlan !== 1
    ? utils.formatPrice(userStore.priceGroups.retail, currentArticle.value._RetailPrice, userStore.activeCatalog?.Config.ShowPriceThousandsSeparated)
    : ''
})

const getDate = computed(() => {
  let date = currentArticle.value._ShipDateRange
  if (userStore.activeCatalog?.Config.ArticleThumbnailShowFirstDeliveryDate) {
    date = utils.formatDate(currentArticle.value.getFirstDeliveryDate(userStore.activeCatalog))
  }

  return date
})

const showBackArrow = computed(() => utils.isDefined(selectedArticleAction.value))

const isArticleActionsEnabled = computed(() => getArticleActions.value.length > 0 && userStore.activeCatalog?.CatalogCode === currentArticle.value.CatalogCode)
  && (!utils.isDefined(userStore.currentCustomer) || !currentArticle.value._IsNonSegmented)

const infoHeaderTitle = computed(() => utils.isDefined(selectedArticleAction.value) ? allArticleActions[selectedArticleAction.value].label : '')

const isUpdateArticleForecastEnabled = computed(() => isForecastModuleEnabled.value && userStore.userProfile.isValidPrivilege(privileges.article.getForecasts))

const allowArticleCacheUpdatedEventStream = computed(() => userStore.userProfile.isValidPrivilege(privileges.article.cacheUpdatedEventStream))

const availableArticleAssetsViewerHeight = computed(() => {
  if (!showOtherColors.value) { return '100%' }
  return 'calc(100% - 200px)'
})
function onSettingsChanged(form: ISettingItem) {
  liveUpdates.value = form.liveUpdates
}

async function onBackClicked() {
  if (selectedArticleAction.value === allArticleActions.edit.key && isUpdateFormDirty()) {
    const dialogResult = await confirmDialog(t('updateArticle.youWantToLeaveThePage'), t('updateArticle.youSaveYourChangesDiscardContinue'), [], undefined, async () => {
      if (appConfig.DB) {
        await appConfig.DB.articleForms.delete([currentArticle.value!.CatalogCode, currentArticle.value!.Id, userStore.userProfile.Id, 'editArticle'])
      }
    }, 'general.save', undefined, true)
    if (dialogResult !== 'cancel') {
      selectedArticleAction.value = null
    }
  }
  else {
    selectedArticleAction.value = null
  }
}

function onArticleUpdated(articles: MyArticle[] | Article[], type = '') {
  const message = getUpdatedMessage(articles, type)

  notificationStore.addNotification({ message, type: 'Success' })
  onBackClicked()

  loadArticle()
}

function onOtherColorArticles(article: MyArticle) {
  emit('change', article)
}

const infoMessageList = computed(() => {
  const infoMessages: string[] = []
  if (currentArticle.value._Allocations.length && utils.isDefined(userStore.currentCustomer) && userStore.currentCustomer.AllocationGroups.length) {
    // Each article has one allocation group assigned
    const allocation = userStore.currentCustomer.AllocationGroups.find(a => a.AllocationGroupId === currentArticle.value._Allocations[0].Id)
    if (utils.isDefined(allocation)) {
      if (utils.isDefined(allocation.MinQuantity) && allocation.MinQuantity !== 0
        && utils.isDefined(allocation.MaxQuantity) && allocation.MaxQuantity !== 0) { infoMessages.push(t('articleDetails.alerts.minAndMaxQuantity', { maxQuantity: allocation.MaxQuantity, minQuantity: allocation.MinQuantity })) }

      else if (utils.isDefined(allocation.MinQuantity) && allocation.MinQuantity !== 0) { infoMessages.push(t('articleDetails.alerts.minQuantityOnly', { minQuantity: allocation.MinQuantity })) }

      else if (utils.isDefined(allocation.MaxQuantity) && allocation.MaxQuantity !== 0) { infoMessages.push(t('articleDetails.alerts.maxQuantityOnly', { maxQuantity: allocation.MaxQuantity })) }
    }
  }
  // show info for weather article have any valid crd and size
  if (isOrderModuleEnabled.value) {
    let validCrd = false
    for (const crd of currentArticle.value._DeliveryDates) {
      if (crd.Status > 0 && userStore.activeCatalog && utils.isDefined(userStore.activeCatalog._IndexedCatalogCRD[crd.CrdId])
        && userStore.activeCatalog._IndexedCatalogCRD[crd.CrdId].Status > 0 && userStore.activeCatalog._IndexedCatalogCRD[crd.CrdId].Availability) {
        const nowInMilliseconds = Date.now()
        if ((crd.AvailabilityFrom == null && crd.AvailabilityTo == null)
          || (((crd.AvailabilityFrom != null && nowInMilliseconds > crd.AvailabilityFrom.getTime()) || (crd.AvailabilityFrom == null))
            && ((crd.AvailabilityTo != null && nowInMilliseconds < crd.AvailabilityTo.getTime()) || (crd.AvailabilityTo == null)))) {
          validCrd = true
          break
        }
      }
    }
    if (!validCrd) { infoMessages.push(t('articleDetails.alerts.unavailableCRD')) }

    const articleSizes = currentArticle.value._Sizes
    if (articleSizes.filter(size => !size.Status).length === articleSizes.length) { infoMessages.push(t('articleDetails.alerts.orderlineWithoutSize')) }
  }
  return infoMessages
})
async function setArticleDetailsData() {
  const detailsData: {
    groups: Record<string, { label: string, value: any, width: number, style?: string[] }[]>
    nonGroup: { label: string, value: any, width: number, style?: string[] }[]
  } = { groups: {}, nonGroup: [] }
  // const groups : Record<string, { data: { label: string, value: any, width: number, style?: string[] }[] , expand: boolean}> = {}
  if (userStore.activeCatalog?.Config.ArticleDetailsTemplate2 && utils.isDefined(userStore.myAttributes)) {
    for (const itm of userStore.activeCatalog.Config.ArticleDetailsTemplate2) {
      if (itm.groupName) {
        if (!detailsData.groups[itm.groupName]) {
          detailsData.groups[itm.groupName] = []
          if (utils.isDefined(itm.expand) && itm.expand) {
            activeGroups.value.push(itm.groupName)
          }
        }
        detailsData.groups[itm.groupName].push({
          label: itm.label,
          value: utils.ifUndefined(await currentArticle.value.translateToken(itm.value, userStore.myAttributes, userStore.activeCatalog, userStore.masterSizeScales, userStore.priceGroups), ''),
          width: itm.width,
          style: itm.style,
        })
      }
      else {
        detailsData.nonGroup.push({
          label: itm.label,
          value: utils.ifUndefined(await currentArticle.value.translateToken(itm.value, userStore.myAttributes, userStore.activeCatalog, userStore.masterSizeScales, userStore.priceGroups), ''),
          width: itm.width,
          style: itm.style,
        })
      }
    }
  }
  articleDetailsData.value = detailsData
}

function onEditFav() {
  emit('onEditFav', currentArticle.value)
}

// TODO: Handle it separately for line plan
async function loadOtherColorArticles() {
  isOtherColorsloading.value = true
  otherColorArticles.value = []
  const catalogDetails = userStore.linkedCatalogDetails[currentArticle.value.CatalogCode] || userStore.activeCatalog

  if (appConfig.DB && userStore.myAttributes && currentArticle.value.ModelNumber && currentArticle.value.ModelNumber !== '') {
    await getArticlesStream(catalogDetails.CatalogCode, { modelId: currentArticle.value.ModelId, status: true, body: { excludeFields: ['Segmentations', 'Prices', 'Sizes', 'DeliveryDates', 'Resources'] } })
      .then(async (responseArticles) => {
        otherColorArticles.value = await appConfig.DB!.convertToMyArticles(catalogDetails, userStore.linkedCatalogDetails, userStore.myAttributes!, userStore.currentUsername, userStore.userProfile.Id, responseArticles, userStore.currentCustomer, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet)
        otherColorArticles.value.forEach(async (art) => {
          otherColorConfiguredAttributeValues.value[art.Id] = await getAttributeValue(art)
        })
      })
      .catch((e) => {
        console.error('Unable to get other color articles', e)
      })
      .finally(() => {
        isOtherColorsloading.value = false
      })
  }
}
async function getAttributeValue(art) {
  if (userStore.activeCatalog?.Config.ArticleDetailsOtherColorsAttribute) {
    let value = art[userStore.activeCatalog?.Config.ArticleDetailsOtherColorsAttribute]
    if (userStore.myAttributes && userStore.myAttributes[userStore.activeCatalog?.Config.ArticleDetailsOtherColorsAttribute]) {
      await utils.getAttributeTypeSpecificValue(userStore.myAttributes[userStore.activeCatalog?.Config.ArticleDetailsOtherColorsAttribute], art, appConfig.DB, userStore.activeCatalog, userStore.priceGroups).then ((response) => {
        value = response
      })
    }
    return value.toString().length > 0 ? value : '-'
  }
  else {
    return '-'
  }
}

function assetsStatusUpdated() {
  loadAssets(true)
}

function designOptionsStatusUpdated(assetId: string, isArticleDesignOption: boolean) {
  emit('designOptionDeleted', assetId, isArticleDesignOption, currentArticle.value)
}

async function loadAssets(isAssetStatusChanged: boolean = false) {
  if (userStore.userProfile.isValidPrivilege(privileges.article.allowBlockedArticleImages) || currentArticle.value.BlockImage === 0) {
    const cat = currentArticle.value.CatalogCode === userStore.activeCatalog?.CatalogCode ? userStore.activeCatalog : userStore.linkedCatalogDetails[currentArticle.value.CatalogCode]
    const res = await utils.tryAsync(getArticleAssets(cat.DuneContext, cat.ContextKey, currentArticle.value.ArticleNumber, userStore.activeCatalog?.Config.NewestImageAssetKeyList))
    if (res.success) {
      currentArticle.value._Assets = res.result
      emit('updated', '_Assets', res.result)
      if (isAssetStatusChanged) {
        emit('articleAssetsUpdated', currentArticle.value.Id, res.result)
      }
    }
  }
}
function swapArticle() {
  const targetArticle = props.article
  emit('swapArticle', targetArticle)
}

function loadArticle() {
  if (currentArticle.value._IsRequestArticle) {
    loadAssets()
  }
  else if (userStore.activeCatalog) {
    loading.value = true
    const catalogDetails = userStore.linkedCatalogDetails[currentArticle.value.CatalogCode] || userStore.activeCatalog
    getArticlesStream(catalogDetails.CatalogCode, { articleId: currentArticle.value.Id })
      .then(async (responseArticles) => {
        if (responseArticles.length) {
          const datasetAttributes = Object.values(userStore.myAttributes!).filter(attr => attr.IsDatasetAttribute)
          const article = new Article(catalogDetails.CatalogCode, catalogDetails.AssignedCatalogAttributes, (responseArticles[0] as any), catalogDetails.DataSourceTypeId, datasetAttributes)
          if (userStore.currentCustomerSegmentations) {
            article._IsNonSegmented = !appConfig.DB!.isArticleSegmented(article._Segmentations, userStore.currentCustomerSegmentations.Segmentations, catalogDetails._IndexedCatalogSegmentation)
          }
          const myArticle = new MyArticle(article, catalogDetails.AssignedCatalogAttributes, props.article._RetailPrice, props.article._WholesalePrice, props.article._OutletPrice, props.article._Resources, true, [], props.article._FavoriteTags, props.article._Allocations, catalogDetails.Season, catalogDetails.DataSourceTypeId, catalogDetails.Config.ArticlesCustomSortOrder, datasetAttributes)
          currentArticle.value = myArticle
          doesArticleLockedDueToAnyModelArticle.value = await getArticlesLocked([myArticle], !!userStore.activeCatalog!.IsPriceByStyleApply)
          articleStore.updateArticle(article.CatalogArticleId, currentArticle.value)
          emit('onUpdatedArticles', [currentArticle.value])
        }

        setArticleDetailsData()
      })
      .catch((e) => {
        console.error(e)
      })
      .finally(() => {
        loading.value = false
      })
  }
}

function initEventSource() {
  if (allowArticleCacheUpdatedEventStream.value) {
    if (eventSource == null) {
      eventSource = new EventSource(`${net.t1v2.baseURL}/catalogs/${currentArticle.value.CatalogCode}/articles/eventstream?articleId=${currentArticle.value.Id}&token=${net.t1v2.token}`)
      eventSource.onerror = (e) => {
        console.error('EventSource error', e)
        if (eventSource?.readyState === EventSource.CLOSED && !explicitlyClosedES) {
          eventSource.close()
          eventSource = null
          setTimeout(initEventSource, 1000)
        }
      }
      eventSource.addEventListener('article-updated', onArticleUpdatedLive)
    }
    if (pEventSource == null) {
      const catalogDetails = userStore.linkedCatalogDetails[currentArticle.value.CatalogCode] || userStore.otherLinkedCatalogDetails[currentArticle.value.CatalogCode] || userStore.activeCatalog
      if (catalogDetails.DataSourceTypeId === 3) {
        pEventSource = new EventSource(`${net.t1v2.baseURL}/catalogs/${catalogDetails.MasterCatalogCode}/articles/eventstream?articleId=${currentArticle.value.Id}&token=${net.t1v2.token}`)
        pEventSource.onerror = (e) => {
          console.error('EventSource error', e)
          if (pEventSource?.readyState === EventSource.CLOSED && !explicitlyClosedES) {
            pEventSource.close()
            pEventSource = null
            setTimeout(initEventSource, 1000)
          }
        }
        pEventSource.addEventListener('article-updated', onParentArticleUpdatedLive)
      }
    }
  }
}

function closeEventSource() {
  eventSource?.close()
  if (eventSource) {
    eventSource.removeEventListener('article-updated', onArticleUpdatedLive)
  }
  pEventSource?.close()
  if (pEventSource) {
    pEventSource.removeEventListener('article-updated', onParentArticleUpdatedLive)
  }
  eventSource = null
  pEventSource = null
  explicitlyClosedES = true
}

function onArticleUpdatedLive(event: MessageEvent) {
  try {
    if (appConfig.DB && userStore.myAttributes && !loading.value) {
      const articleObj = JSON.parse(event.data)
      const catalogDetails = userStore.linkedCatalogDetails[articleObj.CatalogCode] || userStore.activeCatalog
      const datasetAttributes = Object.values(userStore.myAttributes!).filter(attr => attr.IsDatasetAttribute)
      const article = new Article(catalogDetails.CatalogCode, catalogDetails.AssignedCatalogAttributes, articleObj, catalogDetails.DataSourceTypeId, datasetAttributes)
      appConfig.DB.buildMyArticles([article], catalogDetails, utils.getIndexedLinkedCatalogPriceGroupByLowercaseName(userStore.linkedCatalogDetails), userStore.myAttributes, userStore.currentUsername, userStore.userProfile.Id, userStore.currentCustomer, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet)
        .then(async (myArticles) => {
          myArticles.forEach(async (myArticle) => {
            if (myArticle.CatalogArticleId === currentArticle.value.CatalogArticleId) {
              currentArticle.value = myArticle
              doesArticleLockedDueToAnyModelArticle.value = await getArticlesLocked([myArticle], !!userStore.activeCatalog!.IsPriceByStyleApply)
              setArticleDetailsData()
            }
          })
          if (myArticles.length) {
            articleStore.updateArticleBulk(myArticles)
          }
        })
    }
  }
  catch (e) {
    console.warn('Unable to handle article updates using event source', e)
  }
}

function onParentArticleUpdatedLive(event: MessageEvent) {
  try {
    if (appConfig.DB && userStore.myAttributes && !loading.value) {
      const articleObj = JSON.parse(event.data)
      if (articleObj.Id === currentArticle.value.Id) {
        const catalogDetails = userStore.linkedCatalogDetails[currentArticle.value.CatalogCode] || userStore.otherLinkedCatalogDetails[currentArticle.value.CatalogCode] || userStore.activeCatalog
        appConfig.DB.getMyArticlesByIds(catalogDetails, userStore.linkedCatalogDetails, userStore.myAttributes, userStore.currentUsername, userStore.userProfile.Id, [currentArticle.value.Id], userStore.currentCustomer, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet)
          .then((articles) => {
            if (articles && articles.length) {
              currentArticle.value = articles[0]
            }
          })
      }
    }
  }
  catch (e) {
    console.warn('Unable to handle parent article updates using event source', e)
  }
}

onMounted(() => {
  setArticleDetailsData()
  if (showOtherColors.value) {
    loadOtherColorArticles()
  }
  loadAssets()
  if (window.history.state.selectedArticleOnMerchOrWhiteboardForSwap) {
    selectedArticleOnMerchOrWhiteboardForSwap.value = window.history.state.selectedArticleOnMerchOrWhiteboardForSwap
  }
  if (liveUpdates.value) {
    initEventSource()
  }
})

onUnmounted(() => {
  closeEventSource()
})

watch(liveUpdates, (isEnabled) => {
  if (isEnabled) {
    initEventSource()
  }
  else {
    closeEventSource()
  }
})

// when other color article is clicked open that article
watch(() => props.article, (article) => {
  currentArticle.value = article
  loadAssets()
  setArticleDetailsData()
}, { immediate: true })

function isUpdateFormDirty() {
  return updateArticleRef.value?.isFormDirty() || false
}

defineExpose({
  isUpdateFormDirty,
})
</script>
